import { useCallback, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useNotifications } from '@joor/design-system'
import { useNavigate } from 'react-router-dom'

import {
  AsyncAddOrdersToVisualAssortmentDocument,
  AsyncAddOrdersToVisualAssortmentMutation,
  AsyncAddOrdersToVisualAssortmentMutationVariables,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'

import { DATA_ACTIVITY_CENTER } from 'routes/paths'

export const useAsyncAddOrdersToVA = (options?: { showToasts?: boolean }) => {
  const showToasts = options?.showToasts ?? true
  const { addToasts } = useNotifications()
  const navigate = useNavigate()

  const [asyncAddOrdersJobId, setAsyncAddOrdersJobId] = useState<string | null>(
    null,
  )

  const showError = () => {
    if (!showToasts) return
    addToasts([
      {
        type: 'error',
        title: 'Error sending orders',
        description:
          'Something went wrong while sending orders. Please try again later.',
      },
    ])
  }

  const getSuccessToast = (options?: {
    totalOrders?: number
    validOrders?: number
  }) => {
    const { totalOrders, validOrders } = options || {}
    // Show warning if totalOrders !== validOrders, otherwise show info
    const hasPartialOrders =
      totalOrders !== undefined &&
      validOrders !== undefined &&
      totalOrders !== validOrders

    if (hasPartialOrders) {
      return {
        type: 'warning' as const,
        title: `Sending ${validOrders} of ${totalOrders} orders to assortment`,
        description:
          'Note: Products from "Draft" or "Cancelled" orders cannot be added and were excluded.',
        action: {
          label: 'View status here',
          handler: () => navigate(DATA_ACTIVITY_CENTER),
        },
      }
    }

    return {
      type: 'info' as const,
      title: 'Updating assortment',
      description: `We're sending products to the visual assortment. This may take a moment. Feel free to continue working.`,
      action: {
        label: 'View status here',
        handler: () => navigate(DATA_ACTIVITY_CENTER),
      },
    }
  }

  const [mutate, { loading, error }] = useMutation<
    AsyncAddOrdersToVisualAssortmentMutation,
    AsyncAddOrdersToVisualAssortmentMutationVariables
  >(AsyncAddOrdersToVisualAssortmentDocument, {
    client: atlasClient,
  })

  const asyncAddOrdersToVA = async (
    variables: AsyncAddOrdersToVisualAssortmentMutationVariables,
    options?: {
      totalOrders?: number
      validOrders?: number
    },
  ) => {
    try {
      const result = await mutate({
        variables,
        onCompleted: (data) => {
          const jobId = data?.asyncAddOrdersToVisualAssortment?.jobId
          if (jobId) {
            setAsyncAddOrdersJobId(jobId)
            if (showToasts) addToasts([getSuccessToast(options)])
          } else {
            showError()
          }
        },
        onError: (error) => {
          console.error(
            '[VisualAssortment] Async Add Orders to assortment',
            error,
          )
          showError()
        },
      })
      return result
    } catch (error) {
      console.error('[VisualAssortment] Async Add Orders to assortment', error)
      showError()
      throw error
    }
  }

  return {
    asyncAddOrdersToVA,
    asyncAddingOrdersLoading: loading,
    asyncAddingOrdersError: error,
    asyncAddOrdersJobId,
    resetAsyncAddOrdersJobId: useCallback(
      () => setAsyncAddOrdersJobId(null),
      [],
    ),
  }
}
